import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import {
  InvoiceDataPipelineConfigDto,
  InvoiceDataPipelineConfigRequestDto,
  ResponseDtoInvoiceDataPipelineConfigMetadataDto,
  ResponseDtoPageInvoiceDataPipelineConfigDto,
} from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';
import { cacheKeysUtil } from 'src/utils/cacheKeysUtil';

/**
 * Interface for invoice data pipeline config filters
 */
export interface InvoiceDataPipelineConfigFilters {
  modelIds?: string[];
  dataPipelineConfigIds?: string[];
  active?: boolean[];
}

/**
 * Interface for pagination and sorting parameters
 */
export interface InvoiceDataPipelineConfigParams {
  page?: number;
  size?: number;
  sortingColum?: string;
  sortingDirection?: 'ASC' | 'DESC';
}

/**
 * Hook to fetch invoice data pipeline configs with pagination, sorting and filters
 * @param params Pagination and sorting parameters
 * @param filters Optional filters to apply
 * @returns Query result with paginated invoice data pipeline configs
 */
export const useGetInvoiceDataPipelineConfigs = (
  params: InvoiceDataPipelineConfigParams,
  filters?: InvoiceDataPipelineConfigFilters
): UseQueryResult<ResponseDtoPageInvoiceDataPipelineConfigDto> =>
  useQuery(
    ['invoiceDataPipelineConfigs', params, filters],
    async (): Promise<ResponseDtoPageInvoiceDataPipelineConfigDto> => {
      // Build request with pagination parameters
      const requestDto: InvoiceDataPipelineConfigRequestDto = {
        paginationRequest: {
          page: params.page,
          size: params.size,
          sortingColum: params.sortingColum,
          sortingDirection: params.sortingDirection,
        },
      };

      // Add filters if they exist
      if (filters) {
        if (filters.dataPipelineConfigIds?.length) requestDto.dataPipelineConfigIds = filters.dataPipelineConfigIds;

        if (filters.modelIds?.length) requestDto.modelIds = filters.modelIds;

        if (filters.active?.length) requestDto.activeStatuses = filters.active;
      }

      const response = await tFinancialService.api.getInvoiceDataPipelineConfigs(requestDto);
      return response.data;
    },
    {
      keepPreviousData: true,
    }
  );

/**
 * Hook to fetch invoice data pipeline config metadata for filter options
 * @param filters Optional filters to apply when fetching metadata
 * @returns Query result with invoice data pipeline config metadata
 */
export const useGetInvoiceDataPipelineConfigMetaData = (
  filters?: InvoiceDataPipelineConfigFilters
): UseQueryResult<ResponseDtoInvoiceDataPipelineConfigMetadataDto> =>
  useQuery([cacheKeysUtil.getInvoiceDataPipelineConfigMetaData(), filters], async (): Promise<ResponseDtoInvoiceDataPipelineConfigMetadataDto> => {
    // Create request body with filters if they exist
    const requestDto: InvoiceDataPipelineConfigRequestDto = {};

    // Add filters if they exist
    if (filters) {
      if (filters.dataPipelineConfigIds?.length) requestDto.dataPipelineConfigIds = filters.dataPipelineConfigIds;
      if (filters.modelIds?.length) requestDto.modelIds = filters.modelIds;
      if (filters.active?.length) requestDto.activeStatuses = filters.active;
    }

    const response = await tFinancialService.api.getInvoiceDataPipelineConfigMetaData(requestDto);
    return response.data;
  });

/**
 * Hook to save invoice data pipeline config
 * @returns Mutation function for saving invoice data pipeline config
 */
export const useSaveInvoiceDataPipelineConfig = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (dataPipelineConfigDto: InvoiceDataPipelineConfigDto) => tFinancialService.api.createInvoiceDataPipelineConfig(dataPipelineConfigDto),
    {
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(['invoiceDataPipelineConfigs']),
          queryClient.invalidateQueries(cacheKeysUtil.getInvoiceDataPipelineConfigMetaData()),
        ]);
      },
    }
  );
};

/**
 * Hook to delete invoice data pipeline config
 * @returns Mutation function for deleting invoice data pipeline config
 */
export const useDeleteInvoiceDataPipelineConfig = () => {
  const queryClient = useQueryClient();

  return useMutation(async (id: string) => tFinancialService.api.deleteInvoiceDataPipelineConfig(id), {
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries(['invoiceDataPipelineConfigs']),
        queryClient.invalidateQueries(cacheKeysUtil.getInvoiceDataPipelineConfigMetaData()),
      ]);
    },
  });
};
